import { useState, useRef, useEffect } from 'react';

export interface TeamMember {
    id: string;
    name: string;
    photoUrl?: string;
    role?: string;
    isAvailable?: boolean;
}

export interface AgendaFiltersProps {
    teamMembers: TeamMember[];
    selectedTeamMembers: string[];
    onTeamMembersChange: (ids: string[]) => void;

    // Additional filter options
    statusFilters?: string[];
    selectedStatuses?: string[];
    onStatusesChange?: (statuses: string[]) => void;

    services?: { id: string; name: string }[];
    selectedServices?: string[];
    onServicesChange?: (ids: string[]) => void;

    // Callbacks
    onClearAll?: () => void;
    hideTeamSelector?: boolean;
}

const AgendaFilters = ({
    teamMembers,
    selectedTeamMembers,
    onTeamMembersChange,
    statusFilters = ['SCHEDULED', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'NO_SHOW'],
    selectedStatuses = [],
    onStatusesChange,
    services = [],
    selectedServices = [],
    onServicesChange,
    onClearAll,
    hideTeamSelector = false
}: AgendaFiltersProps) => {
    const [showTeamDropdown, setShowTeamDropdown] = useState(false);
    const [showFiltersPanel, setShowFiltersPanel] = useState(false);
    const teamDropdownRef = useRef<HTMLDivElement>(null);
    const filtersPanelRef = useRef<HTMLDivElement>(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
                setShowTeamDropdown(false);
            }
            if (filtersPanelRef.current && !filtersPanelRef.current.contains(event.target as Node)) {
                setShowFiltersPanel(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTeamMemberToggle = (id: string) => {
        if (selectedTeamMembers.includes(id)) {
            onTeamMembersChange(selectedTeamMembers.filter(m => m !== id));
        } else {
            onTeamMembersChange([...selectedTeamMembers, id]);
        }
    };

    const handleSelectAll = () => {
        if (selectedTeamMembers.length === teamMembers.length) {
            onTeamMembersChange([]);
        } else {
            onTeamMembersChange(teamMembers.map(m => m.id));
        }
    };

    const getTeamButtonLabel = () => {
        if (selectedTeamMembers.length === 0) return 'Todos los empleados';
        if (selectedTeamMembers.length === teamMembers.length) return 'Todo el equipo';
        if (selectedTeamMembers.length === 1) {
            const member = teamMembers.find(m => m.id === selectedTeamMembers[0]);
            return member?.name || 'Empleado seleccionado';
        }
        return `${selectedTeamMembers.length} empleados`;
    };

    const statusLabels: Record<string, string> = {
        'SCHEDULED': 'Agendada',
        'CONFIRMED': 'Confirmada',
        'IN_PROGRESS': 'En Progreso',
        'COMPLETED': 'Completada',
        'CANCELLED': 'Cancelada',
        'NO_SHOW': 'No Asistió'
    };

    const statusColors: Record<string, string> = {
        'SCHEDULED': 'bg-yellow-100 text-yellow-700 border-yellow-200',
        'CONFIRMED': 'bg-emerald-100 text-emerald-700 border-emerald-200',
        'IN_PROGRESS': 'bg-orange-100 text-orange-700 border-orange-200',
        'COMPLETED': 'bg-green-100 text-green-700 border-green-200',
        'CANCELLED': 'bg-red-100 text-red-700 border-red-200',
        'NO_SHOW': 'bg-gray-100 text-gray-700 border-gray-200'
    };

    const activeFiltersCount = selectedStatuses.length + selectedServices.length;

    return (
        <div className="flex items-center gap-3">
            {/* Team Selector Dropdown */}
            {!hideTeamSelector && (
                <div className="relative" ref={teamDropdownRef}>
                    <button
                        onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:border-primary/50 hover:shadow-md transition-all duration-200 text-sm font-medium text-gray-700 group"
                    >
                        {/* Team icon */}
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span>{getTeamButtonLabel()}</span>
                        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showTeamDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Team Dropdown Panel */}
                    {showTeamDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                            {/* Header */}
                            <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-transparent border-b border-gray-100">
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold text-gray-900">Miembros del Equipo</span>
                                    <button
                                        onClick={handleSelectAll}
                                        className="text-xs text-primary hover:text-primary/80 font-medium"
                                    >
                                        {selectedTeamMembers.length === teamMembers.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
                                    </button>
                                </div>
                            </div>

                            {/* Members List */}
                            <div className="max-h-72 overflow-y-auto py-2">
                                {teamMembers.map(member => (
                                    <label
                                        key={member.id}
                                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer transition-colors group"
                                    >
                                        {/* Custom Checkbox */}
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={selectedTeamMembers.includes(member.id)}
                                                onChange={() => handleTeamMemberToggle(member.id)}
                                                className="sr-only"
                                            />
                                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${selectedTeamMembers.includes(member.id)
                                                ? 'bg-primary border-primary'
                                                : 'border-gray-300 group-hover:border-primary/50'
                                                }`}>
                                                {selectedTeamMembers.includes(member.id) && (
                                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </div>
                                        </div>

                                        {/* Avatar */}
                                        <div className="relative">
                                            {member.photoUrl ? (
                                                <img src={member.photoUrl} alt={member.name} className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-sm font-bold shadow-sm">
                                                    {member.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                </div>
                                            )}
                                            {/* Availability Indicator */}
                                            {member.isAvailable !== undefined && (
                                                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${member.isAvailable ? 'bg-green-400' : 'bg-gray-400'
                                                    }`} />
                                            )}
                                        </div>

                                        {/* Member Info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                                            {member.role && (
                                                <p className="text-xs text-gray-500 truncate">{member.role}</p>
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>

                            {/* Footer */}
                            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                                <p className="text-xs text-gray-500 text-center">
                                    {selectedTeamMembers.length} de {teamMembers.length} seleccionados
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Advanced Filters Button */}
            <div className="relative" ref={filtersPanelRef}>
                <button
                    onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-200 text-sm font-medium ${activeFiltersCount > 0
                        ? 'bg-primary text-white border-primary hover:bg-primary/90'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-primary/50 hover:shadow-md'
                        }`}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <span>Filtros</span>
                    {activeFiltersCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-white text-primary text-xs font-bold flex items-center justify-center">
                            {activeFiltersCount}
                        </span>
                    )}
                </button>

                {/* Filters Panel */}
                {showFiltersPanel && (
                    <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* Header */}
                        <div className="px-5 py-4 bg-gradient-to-r from-primary/5 to-transparent border-b border-gray-100 flex items-center justify-between">
                            <span className="font-semibold text-gray-900">Todos los filtros</span>
                            {activeFiltersCount > 0 && (
                                <button
                                    onClick={onClearAll}
                                    className="text-xs text-red-500 hover:text-red-600 font-medium"
                                >
                                    Limpiar todo
                                </button>
                            )}
                        </div>

                        <div className="p-5 space-y-6 max-h-96 overflow-y-auto">
                            {/* Status Filters */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Estado de la cita
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {statusFilters.map(status => (
                                        <button
                                            key={status}
                                            onClick={() => {
                                                if (selectedStatuses.includes(status)) {
                                                    onStatusesChange?.(selectedStatuses.filter(s => s !== status));
                                                } else {
                                                    onStatusesChange?.([...selectedStatuses, status]);
                                                }
                                            }}
                                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${selectedStatuses.includes(status)
                                                ? statusColors[status]
                                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            {statusLabels[status] || status}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Services Filter */}
                            {services.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        Servicios
                                    </h4>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {services.map(service => (
                                            <label
                                                key={service.id}
                                                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedServices.includes(service.id)}
                                                    onChange={() => {
                                                        if (selectedServices.includes(service.id)) {
                                                            onServicesChange?.(selectedServices.filter(s => s !== service.id));
                                                        } else {
                                                            onServicesChange?.([...selectedServices, service.id]);
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                                />
                                                <span className="text-sm text-gray-700">{service.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={() => setShowFiltersPanel(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                            >
                                Cerrar
                            </button>
                            <button
                                onClick={() => setShowFiltersPanel(false)}
                                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                            >
                                Aplicar
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgendaFilters;
